import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShoppingBag, Lock, Check, Star, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { DoodleSquiggle } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = { hat: 'Hats', background: 'Backgrounds', outfit: 'Outfits' };
const SEASON_LABELS = { all: 'All', halloween: '🎃 Halloween', christmas: '🎄 Christmas', valentine: '💘 Valentine', spring: '🌸 Spring', summer: '☀️ Summer' };

export default function AccessoryShop({ onUpdate }) {
  const [shopData, setShopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('hat');
  const [activeSeason, setActiveSeason] = useState('all');
  const [buying, setBuying] = useState(null);

  const fetchShop = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/shop`, { withCredentials: true });
      setShopData(data);
    } catch (e) {
      console.error('Failed to fetch shop:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShop(); }, [fetchShop]);

  const handleBuy = async (itemId) => {
    setBuying(itemId);
    try {
      const { data } = await axios.post(`${API}/shop/buy/${itemId}`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchShop();
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    } finally {
      setBuying(null);
    }
  };

  const handleEquip = async (itemId) => {
    try {
      await axios.post(`${API}/shop/equip/${itemId}`, {}, { withCredentials: true });
      toast.success('Accessory updated!');
      fetchShop();
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to equip');
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Loading shop...</div>;
  if (!shopData) return null;

  const filteredItems = shopData.items.filter(i => {
    if (i.category !== activeCategory) return false;
    if (activeSeason === 'all') return true;
    return i.season === activeSeason;
  });

  return (
    <div className="space-y-4" data-testid="accessory-shop">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
          <ShoppingBag className="w-6 h-6" /> Pet Shop
        </h2>
        <div className="flex items-center gap-2 bg-[#FDF0D5] px-4 py-2 rounded-full border-2 border-black">
          <Star className="w-5 h-5 text-yellow-600" fill="currentColor" />
          <span className="font-bold font-['Fredoka']" data-testid="shop-points">{shopData.points}</span>
          <span className="text-xs text-gray-500">points</span>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2">
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            className={`neo-btn px-4 py-2 ${activeCategory === key ? 'bg-[#A2D2FF]' : 'bg-white'}`}
            data-testid={`shop-cat-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Season Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(SEASON_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveSeason(key)}
            className={`neo-btn px-3 py-1.5 text-sm ${activeSeason === key ? 'bg-[#FFC8DD]' : 'bg-white'}`}
            data-testid={`shop-season-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredItems.map(item => (
          <div
            key={item.id}
            className={`neo-card p-4 text-center transition-all ${
              item.equipped ? 'ring-4 ring-yellow-400' : ''
            } ${!item.unlocked ? 'opacity-60' : ''}`}
            style={{ backgroundColor: item.equipped ? item.color + '40' : 'white' }}
            data-testid={`shop-item-${item.id}`}
          >
            {/* Item visual */}
            <div
              className="w-16 h-16 mx-auto mb-3 rounded-2xl border-2 border-black flex items-center justify-center text-3xl"
              style={{ backgroundColor: item.color }}
            >
              {!item.unlocked ? (
                <Lock className="w-8 h-8 text-gray-500" />
              ) : (
                item.emoji
              )}
            </div>

            <h4 className="font-bold font-['Fredoka'] text-sm mb-1">
              {item.name}
              {item.season && (
                <span className="ml-1 text-[10px] bg-black/80 text-white px-1.5 py-0.5 rounded-full">
                  {item.season === 'halloween' ? '🎃'
                    : item.season === 'christmas' ? '🎄'
                    : item.season === 'valentine' ? '💘'
                    : item.season === 'spring' ? '🌸'
                    : item.season === 'summer' ? '☀️'
                    : '✨'}
                </span>
              )}
            </h4>

            {/* Price/Milestone */}
            {item.milestone ? (
              <p className="text-xs text-gray-500 mb-2">
                {item.unlocked ? (
                  <span className="text-green-600 font-bold flex items-center justify-center gap-1">
                    <Sparkles className="w-3 h-3" /> Milestone reward
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" /> {item.milestone.replace(/_/g, ' ')}
                  </span>
                )}
              </p>
            ) : (
              <p className="text-sm font-bold mb-2 flex items-center justify-center gap-1">
                <Star className="w-3 h-3 text-yellow-600" fill="currentColor" />
                {item.price} pts
              </p>
            )}

            {/* Action Button */}
            {item.owned ? (
              <button
                onClick={() => handleEquip(item.id)}
                className={`neo-btn w-full py-1.5 text-sm ${
                  item.equipped ? 'bg-[#FF6B6B] text-white' : 'bg-[#B9FBC0]'
                }`}
                data-testid={`equip-${item.id}`}
              >
                {item.equipped ? 'Unequip' : 'Equip'}
              </button>
            ) : item.unlocked ? (
              <button
                onClick={() => handleBuy(item.id)}
                disabled={buying === item.id || (!item.milestone && shopData.points < item.price)}
                className={`neo-btn w-full py-1.5 text-sm ${
                  !item.milestone && shopData.points < item.price
                    ? 'bg-gray-200 cursor-not-allowed'
                    : 'bg-[#A2D2FF]'
                }`}
                data-testid={`buy-${item.id}`}
              >
                {buying === item.id ? '...' : item.milestone ? 'Claim Free!' : 'Buy'}
              </button>
            ) : (
              <div className="neo-btn w-full py-1.5 text-sm bg-gray-100 cursor-not-allowed text-gray-400 text-center">
                Locked
              </div>
            )}

            {/* Equipped indicator */}
            {item.equipped && (
              <div className="mt-1 flex items-center justify-center gap-1 text-xs text-green-600 font-bold">
                <Check className="w-3 h-3" /> Equipped
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
